import { setLocale as umiSetLocale, getLocale as umiGetLocale, history } from 'umi';
import { setDefaultConfig } from 'antd-mobile';
import zhCNMobile from 'antd-mobile/es/locales/zh-CN';
import zhTWMobile from 'antd-mobile/es/locales/zh-TW';
import enUSMobile from 'antd-mobile/es/locales/en-US';
import esESMobile from 'antd-mobile/es/locales/es-ES';
import enUS from './en-US';
import zhTW from './zh-TW';
import zhCN from './zh-CN';
import esEs from './es-ES';

type AVAILABLE_KEY = 'zh-TW' | 'zh-CN' | 'en-US' | 'es-ES';
type LOCALE_PKG = Record<string, string>;
type AVAILABLE_ID = string | React.ReactNode | (string | React.ReactNode)[];
interface OPTIONS {
  values?: Record<string, any>;
}

const mobileLocale: Record<AVAILABLE_KEY, any> = {
  'zh-TW': zhTWMobile,
  'zh-CN': zhCNMobile,
  'es-ES': esESMobile,
  'en-US': enUSMobile,
};

const getQueryLocale = () => history.location.query.locale as AVAILABLE_KEY;
const getPathName = () => history.location.pathname;
const getLanguageLocale = () => {
  if (getPathName()?.startsWith('/f/')) {
    // 车队数据面板语言跟随浏览器
    return getQueryLocale() || window.navigator.language;
  }
  return getQueryLocale() || umiGetLocale();
};

class Locale {
  private localeMaps: Record<AVAILABLE_KEY, LOCALE_PKG> = {
    'zh-TW': zhTW,
    'zh-CN': zhCN,
    'es-ES': esEs,
    'en-US': enUS,
  };

  private localePkg: LOCALE_PKG = this.localeMaps['en-US'];

  constructor() {
    this.localePkg = this.localeMaps[this.getLocale()];
  }

  setLocalePkg() {
    this.localePkg = this.localeMaps[this.getLocale()];
  }

  setLocale: (locale: AVAILABLE_KEY, realReload: boolean) => void = (locale, realReload) => {
    if (getQueryLocale()) {
      history.replace(getPathName());
    }
    umiSetLocale(locale, realReload);
    setDefaultConfig({ locale: mobileLocale[locale] });
    /**
     * 翻译是依靠this.localePkg 但是之前未更新
     * 会导致只能通过reload 整个才能看到更新效果 体验差
     */
    this.setLocalePkg();
  };

  getLocale: (locale?: string) => AVAILABLE_KEY = (locale = getLanguageLocale()) => {
    const l = locale.toLowerCase();
    const fallbackMap = [
      { key: 'zh-TW', is: () => ['zh-tw', 'zh-hk', 'zh-mo'].includes(l) },
      { key: 'zh-CN', is: () => l.startsWith('zh-') || l === 'zh' },
      { key: 'es-ES', is: () => l.startsWith('es-') || l === 'es' },
      { key: 'en-US', is: () => true },
    ];
    return (fallbackMap.find((_) => _.is()) as { key: string }).key as AVAILABLE_KEY;
  };

  private translateStr: (id: string, options?: OPTIONS) => string = (id, options = {}) => {
    const { values = {} } = options;
    let str = this.localePkg[id] || id;
    Object.keys(values).forEach((key) => {
      const keyword = `\\\${${key}}`;
      str = str.replace(new RegExp(keyword, 'g'), values[key]);
    });
    return str;
  };

  s: (id: AVAILABLE_ID, options?: OPTIONS) => AVAILABLE_ID = (id, options) => {
    if (Array.isArray(id)) {
      return id.map((_) => this.s(_, options));
    }
    if (typeof id !== 'string') {
      return id;
    }
    return this.translateStr(id, options);
  };
}

const localeInstance = new Locale();

const getLocale = localeInstance.getLocale.bind(localeInstance);
const setLocale = localeInstance.setLocale.bind(localeInstance);
const s = localeInstance.s.bind(localeInstance);

export { getLocale, setLocale, s };
